import { Helmet } from "react-helmet-async";

/**
 * Search setup for the new public website.
 * While the site lives under /new-site it is private, so every page is marked
 * noindex. At launch, set NEW_SITE_LIVE = true and drop the /new-site prefix:
 * canonicals already point at the launch addresses on SITE_URL.
 */
export const NEW_SITE_LIVE = true;
export const SITE_URL = "https://snapshot.live";
export const BRAND = "Snapshot Live";

/** Launch addresses (without the private /new-site prefix). */
export const LAUNCH_PATHS = {
  home: "/",
  about: "/about-us",
  solution: "/solution",
  sectors: "/sectors",
  caseStudies: "/case-studies",
  news: "/news",
} as const;

export const SECTOR_SLUGS = ["flood-monitoring", "hostile-vehicle-mitigation", "streetworks", "sites-and-facilities", "construction"] as const;

const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: BRAND,
  url: SITE_URL,
  logo: `${SITE_URL}/snapshot-logo.png`,
  slogan: "Built to go where others can't.",
  description: "Solar-powered 4G site cameras and an image monitoring platform for remote, off-grid sites.",
  areaServed: "GB",
  email: "info@snapshot.live",
  sameAs: ["https://www.linkedin.com/company/108945600/", "https://www.instagram.com/snap_shotlive/"],
  address: {
    "@type": "PostalAddress",
    streetAddress: "DOCK 2/006, 85 Explorations Drive",
    postalCode: "LE4 5NU",
    addressLocality: "Leicester",
    addressCountry: "GB",
  },
  legalName: "Snapshot Live Ltd",
};

interface SeoProps {
  title: string;
  description: string;
  /** Launch path, e.g. "/solution". */
  path: string;
  type?: "website" | "article";
  image?: string;
  /** Extra JSON-LD objects (Article, BreadcrumbList). */
  schema?: object[];
}

export function NewSiteSeo({ title, description, path, type = "website", image, schema = [] }: SeoProps) {
  const url = `${SITE_URL}${path}`;
  const absImage = image && /^https?:\/\//.test(image) ? image : undefined;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {!NEW_SITE_LIVE && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      {absImage && <meta property="og:image" content={absImage} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION)}</script>
      {schema.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export const breadcrumb = (items: [string, string][]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${SITE_URL}${path}`,
  })),
});

export const articleSchema = (headline: string, description: string, path: string, date?: string) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  url: `${SITE_URL}${path}`,
  ...(date ? { datePublished: date } : {}),
  author: { "@type": "Organization", name: BRAND },
  publisher: { "@type": "Organization", name: BRAND, logo: { "@type": "ImageObject", url: `${SITE_URL}/snapshot-logo.png` } },
});

/** Short description from longer copy, trimmed at a word boundary. */
export const clip = (text: string, max = 158) =>
  text.length <= max ? text : `${text.slice(0, max).replace(/\s+\S*$/, "")}…`;
